import { styled } from '@mui/material';

import { useEffect } from 'react';

import { Navigate, NavLink, useSearchParams } from 'react-router';

import { AxiosError } from 'axios';

import { HappyFace, Loader } from '@app-velocity/animations';
import { I18nResources, useI18n } from '@app-velocity/i18n';
import { useAcceptInvitationByTokenMutation } from '@app-velocity/libs/teams-data-access';
import { Alert, Button, Typography } from '@app-velocity/ui';

const StyledContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(5),
  justifyContent: 'center',
  alignItems: 'center',
}));

const StyledTitle = styled(Typography)({
  fontSize: 48,
  lineHeight: 1,
  fontWeight: 500,
});

const getErrorMessage = (
  error: Error | AxiosError<{ data: { details: string } }>
): I18nResources => {
  if (
    'response' in error &&
    error.response?.data &&
    'details' in error.response.data
  ) {
    switch (error.response?.data?.details) {
      case 'Invitation has already been used':
        return 'teams:invitation.already_used';
      case 'Invalid invitation token':
        return 'teams:invitation.invalid_token';
      case 'Invitation has expired':
        return 'teams:invitation.expired';
      case 'User not found':
        return 'teams:invitation.user_not_found';
      case 'User must register first before accepting invitation':
        return 'teams:invitation.must_register_first';
      default:
        return 'teams:invitation.incorrect';
    }
  }

  return 'teams:invitation.other_error';
};

export const Accepted = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const { t } = useI18n();

  const {
    mutate: acceptInvitation,
    isPending,
    error,
  } = useAcceptInvitationByTokenMutation();

  useEffect(() => {
    if (!token) return;
    acceptInvitation(token);
  }, [token]);

  if (isPending) return <Loader />;

  if (error) {
    return (
      <>
        <Alert severity="error">{t(getErrorMessage(error))}</Alert>

        <Button component={NavLink} to="/" variant="contained" color="primary">
          {t('shared:go_to_homepage')}
        </Button>
      </>
    );
  }

  if (!token) return <Navigate to="/" />;

  return (
    <StyledContainer>
      <HappyFace />
      <StyledTitle component="h1">
        {t('teams:invitation.accepted.title')}
      </StyledTitle>

      <Typography variant="body1">
        {t('teams:invitation.accepted.text')}
      </Typography>
      <Button component={NavLink} variant="contained" color="primary" to="/">
        {t('teams:invitation.go_to_report_dashboard')}
      </Button>
    </StyledContainer>
  );
};
